import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BaseEntity } from '@shared/types/base-association';
import { BaseTheme } from '@shared/types/base-theme';
import { tap, map } from 'rxjs/operators';
import { BehaviorSubject, Observable, catchError, throwError } from 'rxjs';
import { environment } from '@environment';
import {
  addNewItemToState,
  updateItemInState,
  deleteItemFromState,
} from '@shared/utils/state.util';
import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root',
})
export class ThemeService<ThemeType extends BaseTheme> {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);

  private _themes = new BehaviorSubject<ThemeType[]>([]);
  private _isLoading = new BehaviorSubject<boolean>(false);
  private _themesSummary = new BehaviorSubject<BaseEntity[]>([]);
  private lastFetchedClientUuid: string | null = null;

  readonly themes$ = this._themes.asObservable();
  readonly themesSummary$ = this._themesSummary.asObservable();
  readonly isLoading$ = this._isLoading.asObservable();

  constructor() {}

  // TODO: need to fix the BE in order to use theme summary
  //  we have an error on saving theme association
  fetchThemeSummary(): Observable<BaseEntity[]> {
    if (this._themesSummary.value.length) {
      return this.themesSummary$;
    }
    this._isLoading.next(true);
    return this.http
      .get<
        BaseEntity[]
      >(environment.backendUrl + `/api/admin/theme/all/summary`)
      .pipe(
        tap((res) => {
          this._themesSummary.next(res);
          this._isLoading.next(false);
        }),
        catchError((err) => {
          this._isLoading.next(false);
          return throwError(() => new Error(err.message));
        }),
      );
  }

  fetchThemes(): Observable<ThemeType[]> {
    if (this._themes.value.length) {
      return this.themes$;
    }
    this._isLoading.next(true);
    return this.http
      .get<{ themes: ThemeType[] }>(environment.backendUrl + '/api/admin/theme')
      .pipe(
        map((response) => response.themes),
        tap((themes) => {
          this._isLoading.next(false);
          this._themes.next(themes);
        }),
        catchError((err: Error) => {
          this._isLoading.next(false);
          return throwError(() => new Error(err.message));
        }),
      );
  }

  fetchThemesIfClientChanged(
    currentClientUuid: string | null,
  ): Observable<ThemeType[] | null> {
    if (this.lastFetchedClientUuid !== currentClientUuid) {
      this.lastFetchedClientUuid = currentClientUuid;
      return this.fetchThemes();
    }
    return new BehaviorSubject<null>(null);
  }

  createSingleTheme(theme: ThemeType): Observable<ThemeType> {
    this._isLoading.next(true);
    return this.http
      .post<ThemeType>(
        environment.backendUrl + '/api/admin/theme/single',
        theme,
      )
      .pipe(
        tap((response) => addNewItemToState(this._themes, response)),
        tap(() => this._isLoading.next(false)),
        tap(() =>
          this.notificationService.showSuccessNotification('Theme created'),
        ),
        catchError((err: HttpErrorResponse) => {
          this._isLoading.next(false);
          this.notificationService.showErrorNotification(
            'Error adding the theme',
          );
          return throwError(
            () => new Error(err?.error?.message || err?.message),
          );
        }),
      );
  }

  updateSingleTheme(theme: ThemeType): Observable<ThemeType> {
    this._isLoading.next(true);
    return this.http
      .put<ThemeType>(
        environment.backendUrl + '/api/admin/theme/single/' + theme.uuid,
        theme,
      )
      .pipe(
        tap((updated) => updateItemInState(this._themes, updated)),
        tap(() => this._isLoading.next(false)),
        tap(() =>
          this.notificationService.showSuccessNotification('Theme updated'),
        ),
        catchError((err: HttpErrorResponse) => {
          this._isLoading.next(false);
          this.notificationService.showErrorNotification(
            'Error updating the theme',
          );
          return throwError(
            () => new Error(err?.error?.message || err?.message),
          );
        }),
      );
  }

  deleteSingleTheme(uuid: string): Observable<void> {
    this._isLoading.next(true);
    return this.http
      .delete<void>(environment.backendUrl + '/api/admin/theme/single/' + uuid)
      .pipe(
        tap(() => {
          this._isLoading.next(false);
          deleteItemFromState(this._themes, uuid);
          this.notificationService.showSuccessNotification(
            'theme.deleteThemeSuccess',
          );
        }),
        catchError((err: Error) => {
          this._isLoading.next(false);
          this.notificationService.showErrorNotification(
            'Error deleting the theme',
          );
          return throwError(() => new Error(err.message));
        }),
      );
  }

  resetData(): void {
    this._themes.next([]);
    this._themesSummary.next([]);
  }
}
